import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import QuizRoundedIcon from '@mui/icons-material/QuizRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import { TOKENS, PageContainer, PageHeader, SectionCard } from '../components/NextGenPortalUI';
import { useNexusBilling } from '../context/NexusBillingContext';

/**
 * NextGenAITutorHub
 *
 * "AI Tutor" landing page inside TSG Nexus. This is a thin navigational
 * index only — it contains no AI logic, no API calls, and no business logic
 * of its own. Every card below routes straight into the SAME AI Tutor
 * components already live in the main learner app (ExplainPage,
 * ResumeReviewPage, InterviewPage, CodeReviewPage, SkillTestPage,
 * ProjectBuilderPage) via /nextgen-builder/ai-tutor/*, unmodified and 100%
 * reused. It exists purely because the learner app has no single hub route
 * for these six tools to link to — the tools themselves are not duplicated.
 */
const TOOLS = [
  {
    key: 'explain',
    subFeature: 'EXPLAIN_DEEP',
    label: 'Explain Deep',
    description: 'Ask any complex topic and get a deep, step-by-step AI explanation.',
    icon: <AutoAwesomeRoundedIcon />,
    path: '/nextgen-builder/ai-tutor/explain',
  },
  {
    key: 'resume-review',
    subFeature: 'RESUME_REVIEW',
    label: 'Resume Review',
    description: 'Get AI feedback on your resume before you apply.',
    icon: <DescriptionRoundedIcon />,
    path: '/nextgen-builder/ai-tutor/resume-review',
  },
  {
    key: 'interview',
    subFeature: 'INTERVIEW_PREP',
    label: 'Interview Prep',
    description: 'Practice interview questions with AI-driven guidance.',
    icon: <RecordVoiceOverRoundedIcon />,
    path: '/nextgen-builder/ai-tutor/interview',
  },
  {
    key: 'code-review',
    subFeature: 'CODE_REVIEW',
    label: 'Code Review',
    description: 'Get your code reviewed by AI for quality and best practices.',
    icon: <CodeRoundedIcon />,
    path: '/nextgen-builder/ai-tutor/code-review',
  },
  {
    key: 'skill-test',
    subFeature: 'SKILL_TEST',
    label: 'Skill Test',
    description: 'Test your knowledge with AI-generated skill assessments.',
    icon: <QuizRoundedIcon />,
    path: '/nextgen-builder/ai-tutor/skill-test',
  },
  // Project Builder is intentionally HIDDEN from the hub (2026-08-07, per
  // product decision). The route /nextgen-builder/ai-tutor/project-builder
  // still exists and works — restore this entry to bring the card back:
  // {
  //   key: 'project-builder',
  //   label: 'Project Builder',
  //   description: 'Get AI guidance while planning and building your project.',
  //   icon: <BuildRoundedIcon />,
  //   path: '/nextgen-builder/ai-tutor/project-builder',
  // },
];

/**
 * Free allowance left for one tool, straight from the server summary
 * (features.AI_TUTOR.monthlyFree.buckets). Returns null when the summary
 * hasn't loaded or the builder is on a legacy plan.
 */
const bucketFor = (aiTutor, subFeature) => {
  if (!aiTutor || aiTutor.legacyAccess || !aiTutor.monthlyFree) return null;
  const buckets = aiTutor.monthlyFree.buckets || [];
  return buckets.find((b) => b.subFeature === subFeature) || null;
};

const ToolCard = ({ tool, onClick, bucket }) => (
  <Grid item xs={12} sm={6} md={4} key={tool.key}>
    <SectionCard
      hoverElevate
      onClick={onClick}
      sx={{ cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
        <Box
          sx={{
            width: 44, height: 44, borderRadius: '12px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: TOKENS.brand.gradient, color: '#FFFFFF',
            boxShadow: TOKENS.shadow.brand, flexShrink: 0,
            '& svg': { fontSize: 22 },
          }}
        >
          {tool.icon}
        </Box>
        <ChevronRightRoundedIcon sx={{ color: '#CBD5E1', fontSize: 20 }} />
      </Box>
      <Typography sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '1rem', mb: 0.5 }}>
        {tool.label}
      </Typography>
      <Typography variant="body2" sx={{ color: TOKENS.text.muted, fontWeight: 500, lineHeight: 1.55 }}>
        {tool.description}
      </Typography>
      {bucket ? (
        <Typography
          variant="caption"
          sx={{ mt: 1.5, fontWeight: 700, color: bucket.remaining <= 2 ? '#B45309' : TOKENS.brand.primary }}
        >
          {bucket.remaining} of {bucket.total} free this month
        </Typography>
      ) : null}
    </SectionCard>
  </Grid>
);

const NextGenAITutorHub = () => {
  const navigate = useNavigate();
  // Every number below is the server's — nothing is computed or assumed here.
  const { features } = useNexusBilling();
  const aiTutor = features ? features.AI_TUTOR : null;
  const paid = aiTutor && aiTutor.paid ? aiTutor.paid : null;
  const unit = aiTutor ? (aiTutor.unitLabelPlural || 'credits') : 'credits';

  return (
    <PageContainer maxWidth={1160}>
      <PageHeader
        icon={<AutoAwesomeRoundedIcon />}
        eyebrow="TSG Nexus · Programs"
        title="AI Tutor"
        description="AI-powered explanations, reviews and practice — the same AI Tutor tools available across The Skill Genie."
      />

      {aiTutor ? (
        <Box sx={{ mb: 2.5 }}>
          <SectionCard sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <BoltRoundedIcon sx={{ color: TOKENS.brand.primary }} />
            <Typography variant="body2" sx={{ color: TOKENS.text.secondary, fontWeight: 700 }}>
              {aiTutor.legacyAccess
                ? 'Full AI Tutor access is included in your current plan.'
                : `${paid ? paid.remaining : 0} paid ${unit} — shared across all five tools, and they never expire at month end.`}
            </Typography>
            <Box
              component="span"
              onClick={() => navigate('/nextgen-builder/plans?feature=AI_TUTOR')}
              sx={{ ml: 'auto', color: TOKENS.brand.primary, fontWeight: 800, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              Recharge
            </Box>
          </SectionCard>
        </Box>
      ) : null}

      <Grid container spacing={2.5}>
        {TOOLS.map((tool) => (
          <ToolCard key={tool.key} tool={tool} bucket={bucketFor(aiTutor, tool.subFeature)} onClick={() => navigate(tool.path)} />
        ))}
      </Grid>
      <Box sx={{ mt: 3 }}>
        <SectionCard sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ArticleRoundedIcon sx={{ color: TOKENS.brand.primary }} />
          <Typography variant="body2" sx={{ color: TOKENS.text.secondary, fontWeight: 500 }}>
            Looking for your saved notes? Open{' '}
            <Box
              component="span"
              onClick={() => navigate('/nextgen-builder/ai-tutor/notes')}
              sx={{ color: TOKENS.brand.primary, fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              TSG Notes
            </Box>.
          </Typography>
        </SectionCard>
      </Box>
    </PageContainer>
  );
};

export default NextGenAITutorHub;
